// ─────────────────────────────────────────────────────────────────────────────
// THE BASELINE STYLESHEET, AND THE :root BLOCK THAT AIMS IT.
//
// Moved out of App.js (where it was 253 lines inside a useEffect) without
// changing what it renders. The rules are preset-INDEPENDENT: every value that
// varies by look is a `var(--ed-…)` reference, so swapping the :root block swaps
// the whole baseline and nothing here has to know which preset is on.
//
// Seven values in the original were literals and so could not be themed — the
// card hover border, the control hover border, two scrollbar greys, the disabled
// button fill, the progress-bar gradient stop, and three rgba values derived by
// hand from the brand and ink. Each is now a token. That is the entire difference
// between "a theme exists" and "a theme can be changed".
//
// ── THE RADII ARE RECORDED, NOT CORRECTED ────────────────────────────────────
//
// The live look uses 8px, 9px, 10px and 12px — four radii with no ladder, which
// is one of the things that makes the app read as unconsidered. They are kept
// exactly, as `r-sm`/`r-md`/`r-lg`/`r-xl` in the `editorial` preset, because
// collapsing them would be a visible change to a shipping product. Every NEW
// preset collapses them to a real ladder. So the token layer carries the defect
// where a reader can see it rather than hiding or silently fixing it.
// ─────────────────────────────────────────────────────────────────────────────

import { TOKEN_KEYS } from "./presets.js";

/** The id of the injected <style> element. Also how `applyTheme` finds it again. */
export const STYLE_ELEMENT_ID = "survai-theme";
/** The id of the injected Google Fonts <link>. */
export const FONT_ELEMENT_ID = "survai-theme-fonts";

/**
 * The `:root` block for a preset.
 *
 * Writes only the keys in TOKEN_KEYS, in that order, so a preset carrying a stray
 * extra key cannot quietly introduce a property nothing declares — the test that
 * asserts preset keys equal TOKEN_KEYS is the one that catches it, and this
 * function not reading the extra key is what keeps the two from disagreeing.
 */
export function rootBlock(preset, { density = 1 } = {}) {
  const t = (preset && preset.tokens) || {};
  const lines = TOKEN_KEYS.map((k) => `        --ed-${k}: ${t[k]};`);
  // Not a TOKEN_KEY: density is the second axis and is chosen independently of
  // the skin, so a preset does not get to set it.
  lines.push(`        --ed-density: ${density};`);
  return `      :root {\n${lines.join("\n")}\n      }`;
}

/**
 * The rules. Identical in effect to what App.js injected, with every
 * preset-dependent literal replaced by its token.
 */
export function baselineCss() {
  return `
      /* ═══ GLOBAL BODY + MAIN ═══ */
      body {
        background: var(--ed-bg) !important;
        color: var(--ed-ink);
        font-family: var(--ed-sans);
        -webkit-font-smoothing: antialiased;
      }
      main { background: var(--ed-bg) !important; }

      /* ═══ CARDS — warm borders, subtle lift on hover ═══ */
      .card {
        background: var(--ed-canvas) !important;
        border: 1px solid var(--ed-line) !important;
        border-radius: var(--ed-r-xl) !important;
        box-shadow: none !important;
        transition: border-color 0.15s ease, transform 0.15s ease !important;
      }
      .card:hover { border-color: var(--ed-line-hover) !important; }

      /* ═══ STAT CARDS ═══ */
      .stat-card {
        background: var(--ed-canvas) !important;
        border: 1px solid var(--ed-line) !important;
        border-radius: var(--ed-r-xl) !important;
        padding: calc(18px * var(--ed-density)) !important;
        box-shadow: none !important;
        transition: border-color 0.15s, transform 0.15s !important;
      }
      .stat-card:hover { border-color: var(--ed-line-hover) !important; transform: translateY(-1px); }
      /* Stat card numbers take the display face */
      .stat-card > div:first-of-type {
        font-family: var(--ed-serif);
        font-weight: 400 !important;
        letter-spacing: -0.02em;
      }

      /* ═══ PRIMARY BUTTON ═══ */
      .btn-primary {
        background: var(--ed-brand) !important;
        color: var(--ed-canvas) !important;
        border: none !important;
        border-radius: var(--ed-r-md) !important;
        font-weight: 600 !important;
        font-family: inherit !important;
        padding: calc(10px * var(--ed-density)) calc(20px * var(--ed-density)) !important;
        cursor: pointer;
        transition: background 0.15s, transform 0.15s !important;
        box-shadow: 0 2px 8px var(--ed-brand-edge) !important;
      }
      .btn-primary:hover:not(:disabled) {
        background: var(--ed-brand-deep) !important;
        transform: translateY(-1px);
      }
      /* ═══ DISABLED: A LIGHT FILL WITH DARK TEXT, NOT WHITE ON GREY ═══
         This was the --ed-disabled fill keeping .btn-primary's white label, plus
         an opacity of 0.7 on top — 1.66:1 to 2.15:1 across the presets
         before the opacity was even applied. WCAG 1.4.3 exempts an inactive
         control, so the contrast guard reported it rather than failing on it, and
         "exempt" was doing a lot of work for a button a nurse still has to read
         to know which action is unavailable.
         Inverted instead: the quiet surface with muted text is already a gated
         pair (muted on line-2, 4.73:1 at its tightest in any preset), so this
         treatment is measured by a check that was already running. It still reads
         as disabled — no shadow, no lift, not-allowed cursor, the quietest text on
         the screen — without the opacity multiplying the problem. */
      .btn-primary:disabled {
        background: var(--ed-line-2) !important;
        color: var(--ed-muted) !important;
        /* --ed-disabled keeps its job, as the edge rather than the fill. Left on
           --ed-line it would have become a token nothing reads, which is how a
           palette grows values no screen can show. */
        border: 1px solid var(--ed-disabled) !important;
        cursor: not-allowed !important;
        transform: none !important;
        box-shadow: none !important;
      }

      /* ═══ SECONDARY BUTTON ═══ */
      .btn-secondary {
        background: var(--ed-canvas) !important;
        color: var(--ed-ink-2) !important;
        border: 1px solid var(--ed-line) !important;
        border-radius: var(--ed-r-md) !important;
        font-weight: 500 !important;
        font-family: inherit !important;
        padding: calc(9px * var(--ed-density)) calc(18px * var(--ed-density)) !important;
        cursor: pointer;
        transition: border-color 0.15s, background 0.12s !important;
      }
      .btn-secondary:hover:not(:disabled) {
        border-color: var(--ed-control-hover) !important;
        background: var(--ed-line-2) !important;
      }

      /* ═══ GHOST BUTTON ═══ */
      .btn-ghost {
        background: transparent !important;
        color: var(--ed-ink-2) !important;
        border: 1px solid transparent !important;
        font-weight: 500 !important;
        font-family: inherit !important;
        cursor: pointer;
      }
      .btn-ghost:hover:not(:disabled) {
        background: var(--ed-line-2) !important;
        color: var(--ed-ink) !important;
      }

      /* ═══ HEADINGS — display face; colour inherits so dark heroes stay light ═══ */
      h1, h2, h3 {
        font-family: var(--ed-serif);
        font-weight: 500;
        letter-spacing: -0.01em;
      }

      /* ═══ FORM FIELDS ═══ */
      .form-field label {
        color: var(--ed-muted);
        font-size: 12px;
        font-weight: 500;
        text-transform: none;
        letter-spacing: normal;
        display: block;
        margin-bottom: 6px;
      }
      .form-field input,
      .form-field select,
      .form-field textarea {
        font-family: inherit;
        border: 1px solid var(--ed-line);
        background: var(--ed-canvas);
        color: var(--ed-ink);
        border-radius: var(--ed-r-sm);
        padding: calc(9px * var(--ed-density)) calc(12px * var(--ed-density));
        transition: border-color 0.15s, box-shadow 0.15s;
        width: 100%;
      }
      .form-field input:focus,
      .form-field select:focus,
      .form-field textarea:focus {
        outline: none;
        border-color: var(--ed-brand);
        box-shadow: 0 0 0 3px var(--ed-brand-glow);
      }

      /* ═══ TABS ═══ */
      .sig-tab {
        background: transparent !important;
        border: none !important;
        border-bottom: 2px solid transparent !important;
        color: var(--ed-muted) !important;
        font-weight: 500 !important;
        font-family: inherit !important;
        padding: calc(10px * var(--ed-density)) calc(16px * var(--ed-density)) !important;
        cursor: pointer;
        transition: color 0.15s !important;
        margin-bottom: -1px;
        border-radius: 0 !important;
      }
      .sig-tab:hover { color: var(--ed-ink-2) !important; }
      .sig-tab.active {
        color: var(--ed-ink) !important;
        border-bottom-color: var(--ed-ink) !important;
        background: transparent !important;
        font-weight: 600 !important;
      }

      /* ═══ TABLES ═══ */
      .table-row {
        border-bottom: 1px solid var(--ed-line);
        transition: background 0.12s;
      }
      .table-row:hover { background: var(--ed-line-2); }

      /* ═══ BADGES / CHIPS ═══ */
      .tag-chip {
        font-family: var(--ed-sans);
        font-weight: 500;
        background: var(--ed-brand-soft) !important;
        color: var(--ed-brand) !important;
        border: 1px solid var(--ed-brand-edge);
        border-radius: var(--ed-r-pill);
      }
      .badge {
        font-family: var(--ed-sans);
        font-weight: 500;
        border-radius: var(--ed-r-pill);
      }

      /* ═══ PROGRESS BARS ═══ */
      .progress-bar {
        background: var(--ed-line) !important;
        border-radius: var(--ed-r-pill) !important;
        overflow: hidden;
      }
      /* brand-lift equal to brand renders this flat. That one token is the whole
         difference between a gradient fill and a solid one. */
      .progress-fill {
        background: linear-gradient(90deg, var(--ed-brand), var(--ed-brand-lift)) !important;
        border-radius: var(--ed-r-pill) !important;
      }

      /* ═══ PIP SECTIONS (POC content) ═══ */
      .pip-section {
        background: var(--ed-canvas);
        border: 1px solid var(--ed-line);
        border-radius: var(--ed-r-xl);
      }

      /* ═══ SECTION HEADERS ═══ */
      .section-header {
        font-family: var(--ed-serif);
        font-weight: 500;
        color: var(--ed-ink);
      }

      /* ═══ PULSE ANIMATION ═══ */
      @keyframes ed-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      .ms-pulse-dot { animation: ed-pulse 1.6s ease-in-out infinite; }

      /* ═══ SCROLLBARS ═══ */
      ::-webkit-scrollbar { width: 10px; height: 10px; }
      ::-webkit-scrollbar-track { background: var(--ed-line-2); }
      ::-webkit-scrollbar-thumb { background: var(--ed-scroll-thumb); border-radius: var(--ed-r-pill); }
      ::-webkit-scrollbar-thumb:hover { background: var(--ed-scroll-thumb-hover); }

      /* ═══ TOAST ═══ */
      .toast {
        font-family: var(--ed-sans);
        border-radius: var(--ed-r-lg);
        box-shadow: 0 8px 24px var(--ed-shadow);
      }
`;
}

/** The whole stylesheet for a preset: the aim, then the rules. */
export function themeCss(preset, { density = 1 } = {}) {
  return `${rootBlock(preset, { density })}\n${baselineCss()}`;
}
